import React, { useState, useEffect, useRef } from 'react';
import { FaUser, FaCloud, FaGraduationCap, FaPlane, FaCar, FaBriefcase } from 'react-icons/fa';
import '../css/hero.css';

const timelineData = [
  {
    icon: FaUser,
    category: 'About Me',
    gradient: 'linear-gradient(135deg, #667eea, #764ba2)',
    content:
      "I'm Joel Fernandes — a software engineer, problem solver, and professional keyboard smasher. I live in the Cloud/DevOps/SRE/Platform space, battling infrastructure so others don't have to.",
  },
  {
    icon: FaCloud,
    category: 'Work',
    gradient: 'linear-gradient(135deg, #4facfe, #00f2fe)',
    content:
      'I\'ve battled the bureaucracy of Fortune 500 companies and helped startups grow from "we have no idea what we\'re doing" to "we kinda know what we\'re doing." My expertise? Mostly AWS, Azure, and Kubernetes that behaves... on good days.',
  },
  {
    icon: FaGraduationCap,
    category: 'Education',
    gradient: 'linear-gradient(135deg, #f093fb, #f5576c)',
    content:
      "I've got a Master's in Computer Science, which mostly means I've mastered the art of Googling under pressure.",
  },
  {
    icon: FaPlane,
    category: 'Aviation',
    gradient: 'linear-gradient(135deg, #43e97b, #38f9d7)',
    content:
      "When I'm not yelling at the cloud, I'm literally in the air. I hold a Private Pilot License! Because apparently, I like things that can crash... uh, I meant servers. Excuse my humour.",
  },
  {
    icon: FaCar,
    category: 'Hobbies',
    gradient: 'linear-gradient(135deg, #fa709a, #fee140)',
    content:
      "Cars, gadgets, and anything with a button that goes *beep*. If it has a manual, LEDs, or a maintenance schedule, I'm interested. As they say, boys don't grow up — our toys just get bigger, pricier, and come with support contracts.",
  },
  {
    icon: FaBriefcase,
    category: 'Open To',
    gradient: 'linear-gradient(135deg, #a18cd1, #fbc2eb)',
    content:
      "Cloud/SRE/Platform roles, aviation chats over coffee, or heated debates about tabs vs spaces (it's tabs, obviously).",
  },
];

function Hero() {
  const [typedText, setTypedText] = useState('');
  const [showContent, setShowContent] = useState(false);
  const heroRef = useRef(null);
  const timelineRef = useRef(null);
  const fullText = 'Hi there! Nice to meet you!';

  useEffect(() => {
    let i = 0;
    let isTyping = true;
    let timeoutId;

    const typeText = () => {
      if (isTyping) {
        if (i < fullText.length) {
          setTypedText(fullText.substring(0, i + 1));
          i++;
          timeoutId = setTimeout(typeText, 50);
        } else {
          isTyping = false;
          timeoutId = setTimeout(typeText, 5000);
        }
      } else {
        if (i > 0) {
          setTypedText(fullText.substring(0, i - 1));
          i--;
          timeoutId = setTimeout(typeText, 30);
        } else {
          isTyping = true;
          timeoutId = setTimeout(typeText, 500);
        }
      }
    };

    typeText();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [fullText]);

  useEffect(() => {
    if (!showContent || !timelineRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('timeline-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const items = timelineRef.current.querySelectorAll('.timeline-item');
    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [showContent]);

  const handleButtonClick = () => {
    if (heroRef.current) {
      heroRef.current.classList.add('move-up');
    }

    setTimeout(() => {
      setShowContent(true);
    }, 300);
  };

  return (
    <section className='hero' ref={heroRef}>
      <div className='hero-content'>
        <div className='profile-pic-container'>
          <img src='/assets/images/profile.png' alt='Joel' />
        </div>

        <h1>
          {typedText}
          <span className='cursor'>|</span>
        </h1>
        {!showContent ? (
          <button className='glow-button' onClick={handleButtonClick}>
            Get to Know Me!
          </button>
        ) : (
          <div className='expanded-content'>
            <div className='timeline' ref={timelineRef}>
              <div className='timeline-line' />
              {timelineData.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    className='timeline-item'
                    key={i}
                    style={{ transitionDelay: `${i * 0.15}s` }}
                  >
                    <div className='timeline-icon' style={{ background: item.gradient }}>
                      <Icon />
                    </div>
                    <div className='timeline-card'>
                      <h3 className='timeline-category'>{item.category}</h3>
                      <p className='timeline-text'>{item.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default Hero;
